import React from "react";
import {
  LinkProps as BaseRouterLinkProps,
  Link as RouterLink,
} from "react-router-dom";
import styled from "styled-components";
import { mediaMax } from "utils/theme";

export type TileSize = "sm" | "md" | "lg" | "xl";

interface StyleProps {
  $size: TileSize;
  $isDisabled?: boolean;
  theme: any;
}

const getBaseStyles = (props) => `
  border: 0;

  text-decoration: none;
  text-align: left;
  font-family: inherit;

  &, &:visited {
    color: ${props.theme.colors.text};
  }
`;

const getStyles = (props: StyleProps) => {
  let styles = "";
  if (props.$isDisabled) {
    styles += `
    background: color-mix(in srgb, ${props.theme.colors.primary} 3%, transparent);
    `;
  } else {
    styles += `
      background: white;
    
      box-shadow: ${props.theme.shadows.large};
    `;
  }
  return styles;
};

const getSizeStyles = (props: StyleProps) => {
  switch (props.$size) {
    case "xl":
      return `
          padding: 48px;

          border-radius: ${props.theme.borderRadius.large}px;

          ${mediaMax("lg")} {
            padding: 24px;

            border-radius: ${props.theme.borderRadius.medium}px;
          }
        `;
    case "lg":
      return `
          padding: 24px;

          border-radius: ${props.theme.borderRadius.large}px;

          ${mediaMax("lg")} {
            padding: 16px;

            border-radius: ${props.theme.borderRadius.small}px;
          }
        `;
    case "md":
      return `
          padding: 16px;

          border-radius: ${props.theme.borderRadius.small}px;
      `;
    case "sm":
      return `
          padding: 16px;

          border-radius: ${props.theme.borderRadius.small}px;
      `;
    default:
      return "";
  }
};

const StyledTile = styled.div<StyleProps>`
  ${getBaseStyles}
  ${getStyles}
  ${getSizeStyles}
`;

const StyledTileInteractive = styled.div<StyleProps>`
  ${getBaseStyles}
  ${getStyles}
  ${getSizeStyles}

  ${(props) =>
    !props.$isDisabled &&
    `  
  &:not([disabled]) {
    cursor: pointer;
  }`}
`;

interface BaseProps {
  size?: TileSize;
  children?: React.ReactNode;
  isDisabled?: boolean;
}

const Tile: React.FC<BaseProps> = ({ size = "md", isDisabled, ...props }) => (
  <StyledTile $size={size} $isDisabled={isDisabled} {...props} />
);

export interface LinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>,
    BaseProps {}

export const TileLink: React.FC<LinkProps> = ({
  size = "md",
  isDisabled,
  href,
  ...props
}) => (
  <StyledTileInteractive
    as="a"
    $size={size}
    $isDisabled={isDisabled}
    data-turbo="false"
    {...props}
    href={isDisabled ? null : href}
  />
);

export interface RouterLinkProps extends BaseRouterLinkProps, BaseProps {}

export const TileRouterLink: React.FC<RouterLinkProps> = ({
  size = "md",
  isDisabled,
  to,
  ...props
}) => (
  <StyledTileInteractive
    as={RouterLink}
    $size={size}
    $isDisabled={isDisabled}
    data-turbo="false"
    {...props}
    to={isDisabled ? null : to}
  />
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    BaseProps {}

export const TileButton: React.FC<ButtonProps> = ({
  size = "md",
  isDisabled,
  ...props
}) => (
  <StyledTileInteractive
    as="button"
    type="button"
    $size={size}
    $isDisabled={isDisabled}
    {...props}
  />
);

export default Tile;
